import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle, MapPin, Phone, ImageIcon, ExternalLink, ChevronLeft,
  GraduationCap, Briefcase, FileBadge, Shield, HelpCircle, Sparkles,
  MessageCircle, User, Stethoscope,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { DoctorSEO } from '../components/DoctorSEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GEO_STORAGE_KEY = 'bd_geo';

function photoUrl(photo) {
  if (!photo) return null;
  return photo.startsWith('http') ? photo : `${API_URL}${photo}`;
}

function whatsappUrl(doctor) {
  const raw = doctor?.whatsapp || doctor?.phone;
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  const number = digits.startsWith('55') ? digits : `55${digits}`;
  return `https://wa.me/${number}`;
}

function getVisitorLocation() {
  try {
    const data = JSON.parse(window.localStorage.getItem(GEO_STORAGE_KEY) || 'null');
    return {
      visitor_city: data?.city || '',
      visitor_state: data?.state || '',
    };
  } catch {
    return { visitor_city: '', visitor_state: '' };
  }
}

function PublicSection({ title, icon: Icon, hint, isEmpty, emptyHint, children, testId, className = '' }) {
  return (
    <section className={`rounded-2xl border border-slate-100 bg-white p-5 md:p-6 ${className}`} data-testid={testId}>
      <div className="mb-3">
        <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900">
          {Icon && <Icon className="w-5 h-5 text-primary" />}
          {title}
        </h3>
        {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
      </div>
      {isEmpty ? <p className="text-sm text-slate-500 italic">{emptyHint}</p> : children}
    </section>
  );
}

function HeaderCard({ doctor }) {
  const waUrl = whatsappUrl(doctor);
  const addressLine = [doctor.address, doctor.city, doctor.state].filter(Boolean).join(' - ');
  const isVerifiedProfile = doctor.profile_trust_level === 'verificado';
  const registrationType = String(doctor.professional_registration_type || (doctor.crm ? 'crm' : '')).toUpperCase();
  const registrationNumber = doctor.professional_registration_number || doctor.crm;
  const registrationUf = doctor.professional_registration_uf || doctor.crm_state;
  const logEvent = (eventType) => {
    axios.post(`${API_URL}/api/public/profile-event`, {
      doctor_id: doctor.id,
      event_type: eventType,
      source: 'doctor_profile',
      ...getVisitorLocation(),
    }).catch(() => {});
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-emerald-100 bg-white" data-testid="public-profile-header">
      <div className="h-1.5 bg-gradient-to-r from-primary via-emerald-400 to-amber-300" />
      <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_21rem]">
        <div className="p-5 md:p-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            <div className="h-28 w-28 flex-shrink-0 overflow-hidden rounded-2xl bg-slate-100 flex items-center justify-center ring-1 ring-slate-100 md:h-36 md:w-36">
              {doctor.photo ? (
                <img src={photoUrl(doctor.photo)} alt={doctor.full_name} className="h-full w-full object-cover" />
              ) : (
                <ImageIcon className="h-12 w-12 text-slate-300" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                {isVerifiedProfile && (
                  <Badge className="border border-emerald-100 bg-emerald-50 text-emerald-700">
                    <CheckCircle className="mr-1 h-3 w-3" />
                    Perfil verificado
                  </Badge>
                )}
                {doctor.is_premium && (
                  <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                    <Sparkles className="mr-1 h-3 w-3" />
                    Perfil premium
                  </Badge>
                )}
              </div>

              <h1 className="text-2xl font-bold text-slate-950 md:text-4xl">{doctor.full_name}</h1>
              <p className="mt-2 flex items-center gap-2 text-base font-medium text-primary md:text-xl">
                <Stethoscope className="h-4 w-4 flex-shrink-0 md:h-5 md:w-5" />
                <span>{doctor.specialty_name || 'Especialidade'} {doctor.city ? `em ${doctor.city}` : ''}</span>
              </p>

              <div className="mt-4 flex flex-wrap gap-2 text-sm">
                {registrationNumber && (
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-slate-700">
                    {registrationType || 'Registro'} {registrationNumber}{registrationUf ? ` / ${registrationUf}` : ''}
                  </span>
                )}
                {doctor.rqe && (
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-slate-700">
                    RQE {doctor.rqe}
                  </span>
                )}
                {doctor.subspecialty && (
                  <span className="rounded-full border border-primary/15 bg-primary/5 px-3 py-1.5 text-primary">
                    {doctor.subspecialty}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <aside className="border-t border-emerald-100 bg-emerald-50/45 p-5 md:p-8 lg:border-l lg:border-t-0">
          <p className="text-xs font-semibold uppercase text-emerald-800">Atendimento</p>
          <div className="mt-4 space-y-4 text-sm text-slate-700">
            {addressLine && (
              <a
                href={doctor.maps_url || `https://www.google.com/maps/search/${encodeURIComponent([doctor.full_name, doctor.address, doctor.city, doctor.state].filter(Boolean).join(', '))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 transition-colors hover:text-primary"
                data-testid="doctor-address-link"
              >
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <span className="min-w-0 flex-1 leading-relaxed">{addressLine}</span>
                <ExternalLink className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
              </a>
            )}
            {doctor.phone && (
              <div className="space-y-3">
                <a href={`tel:${doctor.phone}`} onClick={() => logEvent('phone_click')} className="inline-flex items-center gap-3 font-medium transition-colors hover:text-primary" data-testid="doctor-phone-link">
                  <Phone className="h-4 w-4 text-primary" />
                  <span>{doctor.phone}</span>
                </a>
                {doctor.is_premium && waUrl && (
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => logEvent('whatsapp_click')}
                    className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700"
                    aria-label="Conversar pelo WhatsApp"
                    data-testid="doctor-whatsapp-link"
                  >
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp
                  </a>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}

function BioSection({ doctor }) {
  return (
    <PublicSection
      title={"Sobre o m\u00e9dico"}
      icon={User}
      isEmpty={!doctor.bio}
      emptyHint={"Informa\u00e7\u00f5es sobre o profissional ainda n\u00e3o dispon\u00edveis."}
      testId="public-bio"
    >
      <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{doctor.bio}</p>
    </PublicSection>
  );
}

function CredentialsSection({ doctor }) {
  const isEmpty = !doctor.formacao && !doctor.experiencia && !doctor.rqe;
  return (
    <PublicSection
      title={"Credenciais e forma\u00e7\u00e3o"}
      icon={GraduationCap}
      isEmpty={isEmpty}
      emptyHint={"Forma\u00e7\u00e3o e experi\u00eancia ainda n\u00e3o informadas."}
      testId="public-credentials"
    >
      <div className="grid md:grid-cols-2 gap-4">
        {doctor.formacao && (
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5">
              <GraduationCap className="w-4 h-4 text-slate-400" /> {"Forma\u00e7\u00e3o"}
            </h4>
            <p className="text-sm text-slate-600 whitespace-pre-line">{doctor.formacao}</p>
          </div>
        )}
        {doctor.experiencia && (
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5">
              <Briefcase className="w-4 h-4 text-slate-400" /> {"Experi\u00eancia"}
            </h4>
            <p className="text-sm text-slate-600 whitespace-pre-line">{doctor.experiencia}</p>
          </div>
        )}
        {doctor.rqe && (
          <div className="md:col-span-2">
            <h4 className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5">
              <FileBadge className="w-4 h-4 text-slate-400" /> RQE
            </h4>
            <p className="text-sm text-slate-600">{doctor.rqe}</p>
          </div>
        )}
      </div>
    </PublicSection>
  );
}

function SpecialtySection({ doctor }) {
  return (
    <PublicSection
      title={"Especialidade e \u00e1reas de atua\u00e7\u00e3o"}
      icon={Sparkles}
      isEmpty={!doctor.specialty_name && !doctor.subspecialty}
      emptyHint={"Especialidades ainda n\u00e3o informadas."}
      testId="public-specialty"
    >
      <div className="flex flex-wrap items-center gap-2">
        {doctor.specialty_name && <Badge variant="secondary">{doctor.specialty_name}</Badge>}
        {doctor.subspecialty && <Badge className="bg-primary/10 text-primary border-primary/20">{doctor.subspecialty}</Badge>}
      </div>
    </PublicSection>
  );
}

function PlansSection({ doctor }) {
  const plans = doctor.accepted_health_plans || [];
  return (
    <PublicSection
      title={"Conv\u00eanios e planos de sa\u00fade"}
      icon={Shield}
      isEmpty={plans.length === 0}
      emptyHint={"Conv\u00eanios aceitos ainda n\u00e3o informados."}
      testId="public-plans"
    >
      <div className="flex flex-wrap gap-2">
        {plans.map((plan) => <Badge key={plan} variant="secondary">{plan}</Badge>)}
      </div>
    </PublicSection>
  );
}

function AddressesSection({ doctor }) {
  const addresses = Array.isArray(doctor.addresses) && doctor.addresses.length > 0
    ? doctor.addresses
    : (doctor.address || doctor.city || doctor.phone)
      ? [{
          label: doctor.provider_name || 'Endere\u00e7o principal',
          address: doctor.address,
          city: doctor.city,
          state: doctor.state,
          phone: doctor.phone,
          is_primary: true,
        }]
      : [];

  return (
    <PublicSection
      title={"Endere\u00e7os de atendimento"}
      icon={MapPin}
      hint="Locais informados pelo profissional para atendimento presencial."
      isEmpty={addresses.length === 0}
      emptyHint={"Endere\u00e7os de atendimento ainda n\u00e3o informados."}
      testId="public-addresses"
    >
      <div className="grid gap-3 md:grid-cols-2">
        {addresses.map((addr, idx) => {
          const line = [addr.address, addr.neighborhood, addr.city, addr.state].filter(Boolean).join(', ');
          const mapUrl = `https://www.google.com/maps/search/${encodeURIComponent([doctor.full_name, line].filter(Boolean).join(', '))}`;
          return (
            <div key={addr.id || `addr-${idx}`} className="rounded-xl border border-slate-100 bg-slate-50/50 p-4 md:p-5" data-testid={`public-address-${idx}`}>
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white text-primary ring-1 ring-slate-100">
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">{addr.label || `Endere\u00e7o ${idx + 1}`}</p>
                    {addr.is_primary && <Badge variant="outline" className="py-0 text-[10px]">Principal</Badge>}
                  </div>
                  {line && (
                    <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="block text-sm leading-relaxed text-slate-600 transition-colors hover:text-primary hover:underline">
                      {line}
                    </a>
                  )}
                  {addr.phone && (
                    <a href={`tel:${addr.phone}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                      <Phone className="h-3.5 w-3.5" />
                      {addr.phone}
                    </a>
                  )}
                  {addr.notes && <p className="mt-2 whitespace-pre-line text-xs text-slate-500">{addr.notes}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </PublicSection>
  );
}

function getPatientGuidance(doctor) {
  const specialty = (doctor?.specialty_name || '').toLowerCase();
  const specialtyLabel = doctor?.specialty_name || 'esta especialidade';

  const guides = {
    cardiologista: {
      conditions: ['Dor no peito', 'Falta de ar', 'Press\u00e3o alta', 'Palpita\u00e7\u00f5es', 'Colesterol elevado'],
      when: 'Procure avalia\u00e7\u00e3o se tiver sintomas cardiovasculares, hist\u00f3rico familiar, press\u00e3o alta, diabetes ou necessidade de check-up do cora\u00e7\u00e3o.',
      exams: ['Eletrocardiograma', 'Ecocardiograma', 'Teste ergom\u00e9trico', 'MAPA ou Holter', 'Exames de sangue direcionados'],
    },
    pediatra: {
      conditions: ['Febre em crian\u00e7as', 'Acompanhamento do crescimento', 'Vacina\u00e7\u00e3o', 'Queixas respirat\u00f3rias', 'Orienta\u00e7\u00e3o alimentar'],
      when: 'Procure atendimento para acompanhamento de rotina, d\u00favidas sobre desenvolvimento, vacina\u00e7\u00e3o ou sintomas persistentes em crian\u00e7as e adolescentes.',
      exams: ['Avalia\u00e7\u00e3o cl\u00ednica', 'Curvas de crescimento', 'Triagens preventivas', 'Solicita\u00e7\u00e3o de exames conforme idade e queixa'],
    },
    dermatologista: {
      conditions: ['Manchas na pele', 'Acne', 'Queda de cabelo', 'Alergias cut\u00e2neas', 'Avalia\u00e7\u00e3o de sinais'],
      when: 'Procure avalia\u00e7\u00e3o ao notar les\u00f5es novas, altera\u00e7\u00f5es em pintas, coceira persistente, acne intensa ou d\u00favidas sobre cuidados com a pele.',
      exams: ['Dermatoscopia', 'Bi\u00f3psia de pele quando indicada', 'Avalia\u00e7\u00e3o cl\u00ednica dermatol\u00f3gica', 'Mapeamento de sinais'],
    },
    ginecologista: {
      conditions: ['Rotina ginecol\u00f3gica', 'Preven\u00e7\u00e3o', 'Dor p\u00e9lvica', 'Altera\u00e7\u00f5es menstruais', 'Planejamento reprodutivo'],
      when: 'Procure avalia\u00e7\u00e3o para consultas preventivas, sintomas ginecol\u00f3gicos, acompanhamento de exames ou orienta\u00e7\u00e3o sobre sa\u00fade da mulher.',
      exams: ['Exame preventivo', 'Ultrassonografia quando indicada', 'Exames hormonais', 'Rastreamentos conforme idade e hist\u00f3rico'],
    },
  };

  const matchKey = Object.keys(guides).find((key) => specialty.includes(key));
  return guides[matchKey] || {
    conditions: ['Avalia\u00e7\u00e3o de sintomas', 'Acompanhamento preventivo', 'Orienta\u00e7\u00e3o cl\u00ednica', 'Encaminhamentos quando necess\u00e1rios'],
    when: 'Procure ' + specialtyLabel + ' quando houver sintomas persistentes, necessidade de avalia\u00e7\u00e3o especializada ou orienta\u00e7\u00e3o sobre preven\u00e7\u00e3o e tratamento.',
    exams: ['Consulta cl\u00ednica', 'Avalia\u00e7\u00e3o de hist\u00f3rico de sa\u00fade', 'Solicita\u00e7\u00e3o de exames conforme indica\u00e7\u00e3o', 'Plano de cuidado individualizado'],
  };
}

function PatientGuideSection({ doctor }) {
  const guide = getPatientGuidance(doctor);
  return (
    <PublicSection
      title="Guia para o paciente"
      icon={HelpCircle}
      hint={"Informa\u00e7\u00f5es pr\u00e1ticas para ajudar voc\u00ea a entender se este perfil combina com sua necessidade."}
      isEmpty={false}
      testId="public-patient-guide"
      className="h-full"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">{"Condi\u00e7\u00f5es e queixas relacionadas"}</h4>
          <div className="flex flex-wrap gap-2">
            {guide.conditions.map((item) => (
              <span key={item} className="rounded-full bg-emerald-50 px-3 py-1 text-sm text-emerald-800">
                {item}
              </span>
            ))}
          </div>
        </div>
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Quando procurar</h4>
          <p className="text-sm leading-relaxed text-slate-600">{guide.when}</p>
        </div>
        <div className="md:col-span-2">
          <h4 className="mb-2 text-sm font-semibold text-slate-900">{"Exames e avalia\u00e7\u00f5es comuns"}</h4>
          <div className="flex flex-wrap gap-2">
            {guide.exams.map((item) => (
              <span key={item} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm text-slate-700">
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </PublicSection>
  );
}

function FaqsSection({ doctor, faq }) {
  const ownFaqs = (Array.isArray(doctor.faqs) ? doctor.faqs : [])
    .filter(f => f && (f.question || f.q) && (f.answer || f.a))
    .map((f, i) => ({ question: f.question || f.q, answer: f.answer || f.a, id: f.id || `own-${i}` }));
  const fallbackFaqs = (faq || []).map((f, i) => ({ question: f.q, answer: f.a, id: `seo-${i}` }));
  const list = ownFaqs.length > 0 ? ownFaqs : fallbackFaqs;

  return (
    <PublicSection
      title="Perguntas frequentes"
      icon={HelpCircle}
      isEmpty={list.length === 0}
      emptyHint={"Perguntas frequentes ainda n\u00e3o informadas."}
      testId="public-faqs"
      className="h-full"
    >
      <ul className="space-y-3">
        {list.map((item) => (
          <li key={item.id} className="border-l-2 border-primary/20 pl-3">
            <p className="text-sm font-medium text-slate-800">{item.question}</p>
            <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{item.answer}</p>
          </li>
        ))}
      </ul>
    </PublicSection>
  );
}

export default function DoctorProfilePage({ overrideId }) {
  const params = useParams();
  const id = overrideId || params.id;
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [faq, setFaq] = useState([]);
  const [faqSpecialty, setFaqSpecialty] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoCity, setSeoCity] = useState('');

  const fetchDoctorData = useCallback(async () => {
    setLoading(true);
    try {
      const doctorRes = await axios.get(`${API_URL}/api/public/doctors/${id}`);
      setDoctor(doctorRes.data);

      axios.get(`${API_URL}/api/public/doctors/${id}/related?limit=3`)
        .then(res => {
          setFaq(res.data.faq || []);
          setFaqSpecialty(res.data.specialty_name || '');
          setSeoDescription(res.data.seo_description || '');
          setSeoCity(res.data.city || '');
        })
        .catch(() => {});
    } catch (error) {
      toast.error('Erro ao carregar dados do m\u00e9dico');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDoctorData();
  }, [fetchDoctorData]);

  useEffect(() => {
    if (!doctor?.id) return;
    axios.post(`${API_URL}/api/public/profile-event`, {
      doctor_id: doctor.id,
      event_type: 'profile_view',
      source: 'doctor_profile',
      ...getVisitorLocation(),
    }).catch(() => {});
  }, [doctor?.id]);

  useEffect(() => {
    if (doctor?.slug && !overrideId && window.location.pathname.startsWith('/medico/')) {
      window.history.replaceState(null, '', `/${doctor.slug}`);
    }
  }, [doctor, overrideId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="container-app py-8">
          <div className="max-w-6xl mx-auto">
            <Skeleton className="h-40 rounded-2xl mb-6" />
            <Skeleton className="h-32 rounded-2xl mb-4" />
            <Skeleton className="h-32 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="container-app py-16 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">{"M\u00e9dico n\u00e3o encontrado"}</h1>
          <Link to="/buscar">
            <Button>Voltar para busca</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="doctor-profile-page">
      <DoctorSEO
        doctor={doctor}
        specialty={faqSpecialty}
        city={seoCity}
        state={doctor.state}
        slug={doctor.slug}
        seoDescription={seoDescription}
      />
      <Navbar />

      <div className="container-app py-8">
        <div className="max-w-6xl mx-auto">
          <Link
            to={`/buscar?${new URLSearchParams({
              ...(doctor.specialty_id ? { specialty_id: doctor.specialty_id } : {}),
              ...(doctor.specialty_name || faqSpecialty ? { specialty_name: doctor.specialty_name || faqSpecialty } : {}),
              ...(doctor.state ? { state: doctor.state } : {}),
              ...(doctor.city ? { city: doctor.city } : {}),
            }).toString()}`}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-primary mb-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Voltar para busca
          </Link>

          <div className="mb-6">
            <HeaderCard doctor={doctor} />
          </div>

          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1.25fr)_minmax(20rem,.75fr)]">
            <div className="space-y-5">
              <BioSection doctor={doctor} />
              <CredentialsSection doctor={doctor} />
              <AddressesSection doctor={doctor} />
            </div>
            <div className="space-y-5">
              <SpecialtySection doctor={doctor} />
              <PlansSection doctor={doctor} />
            </div>
          </div>

          <div className="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <FaqsSection doctor={doctor} faq={faq} />
            <PatientGuideSection doctor={doctor} />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
